import React from "react";
import { motion } from "framer-motion";
import { User } from "lucide-react";
import Wordmark from "./Wordmark";

// Intentional entry (TasteTestNow-style). "PICK YOUR TERRITORY." — one tap begins.
// This is NOT a competence test and NOT onboarding/settings: the user is choosing
// the cultural territory they want to PLAY in. Locked terminology: FILM / SERIES /
// GAMES / SCREEN / CROSS-MEDIA. Shared links (challenge / face-off) bypass this
// screen entirely (never make a recipient start from zero).
//
// Visual grammar (Run 4 lock): NO centered color patch. Each card carries a full
// rounded PERIMETER accent — semantic, restrained, illuminated on hover/press.
//   FILM        = single signal-red perimeter
//   SERIES      = single cool-white / icy-silver perimeter
//   GAMES       = single signal-blue perimeter
//   SCREEN      = red -> blue perimeter gradient
//   CROSS-MEDIA = red -> bone/white -> blue perimeter gradient
// single-domain = one territory; combined-domain = blended territory.

const RED = "#ff2233";
const BLUE = "#17b0ff";
const ICE = "#c8d6eb";
const BONE = "#f4f1ea";

// perimeter = the masked border-only gradient; bloom = the soft illuminated halo.
// Exported so other surfaces (e.g. the Profile scope pills) can reuse the
// exact same category colour language instead of inventing their own.
export const ACCENTS = {
  FILM: {
    perimeter: `linear-gradient(150deg, ${RED}, #b3121e)`,
    bloom: `radial-gradient(closest-side, rgba(255,34,51,0.55), transparent)`,
  },
  SERIES: {
    perimeter: `linear-gradient(150deg, #e7eefb, #9fb0c8)`,
    bloom: `radial-gradient(closest-side, rgba(200,214,235,0.42), transparent)`,
  },
  GAMES: {
    perimeter: `linear-gradient(150deg, ${BLUE}, #0a6fb3)`,
    bloom: `radial-gradient(closest-side, rgba(23,176,255,0.5), transparent)`,
  },
  SCREEN: {
    perimeter: `linear-gradient(100deg, ${RED}, ${BLUE})`,
    bloom: `linear-gradient(100deg, rgba(255,34,51,0.5), rgba(23,176,255,0.5))`,
  },
  "CROSS-MEDIA": {
    perimeter: `linear-gradient(100deg, ${RED}, ${BONE} 50%, ${BLUE})`,
    bloom: `linear-gradient(100deg, rgba(255,34,51,0.5), rgba(244,241,234,0.4), rgba(23,176,255,0.5))`,
  },
};

const PRIMARY = [
  { id: "FILM", label: "FILM", sub: "Film only" },
  { id: "SERIES", label: "SERIES", sub: "TV & streaming" },
  { id: "GAMES", label: "GAMES", sub: "Videogames" },
];

const COMBINED = [
  { id: "SCREEN", label: "SCREEN", sub: "Film + Series" },
  { id: "CROSS-MEDIA", label: "CROSS-MEDIA", sub: "Film + Series + Games" },
];

// border-only gradient via mask compositing (paints just the perimeter ring)
const MASK = {
  WebkitMask:
    "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
  WebkitMaskComposite: "xor",
  maskComposite: "exclude",
};

function AccentCard({ option, layout, onSelect }) {
  const a = ACCENTS[option.id];
  const isRow = layout === "row";
  return (
    <button
      type="button"
      data-testid={`select-${option.id.toLowerCase().replace("-", "")}`}
      onClick={() => onSelect(option.id)}
      className={`group relative overflow-visible rounded-2xl bg-[#0b0c11] transition-transform duration-300 ease-out active:scale-[0.98] hover:-translate-y-[2px] focus:outline-none ${
        isRow
          ? "px-4 py-4 flex flex-col items-center justify-center text-center"
          : "px-3 py-6 flex flex-col items-center justify-center text-center"
      }`}
    >
      {/* illuminated bloom (idle: off; hover/press/focus: soft halo) */}
      <span
        aria-hidden
        className="pointer-events-none absolute -inset-[6px] rounded-[22px] opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-45 group-focus:opacity-45 group-active:opacity-60"
        style={{ background: a.bloom }}
      />
      {/* full rounded perimeter accent (idle: restrained; hover/press: intensified) */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-2xl p-[1.5px] opacity-40 transition-opacity duration-300 group-hover:opacity-100 group-focus:opacity-100 group-active:opacity-100"
        style={{ background: a.perimeter, ...MASK }}
      />
      {/* dark interior sheen */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-[1.5px] rounded-[14px]"
        style={{
          background:
            "radial-gradient(120% 100% at 50% 0%, rgba(255,255,255,0.04), transparent 60%)",
        }}
      />
      <span className="relative z-10 font-display font-black uppercase tracking-[0.02em] text-bone text-[15px]">
        {option.label}
      </span>
      <span
        className={`relative z-10 font-body text-[10px] tracking-wide text-silver leading-tight ${
          isRow ? "mt-0.5" : "mt-1"
        }`}
      >
        {option.sub}
      </span>
    </button>
  );
}

export default function TestSelector({ onSelect, onLegal, hasEvidence = false, onProfile, onKeepers, onEditions, onLogIn }) {
  const pick = (id) => onSelect && onSelect(id);
  const legal = (page) => onLegal && onLegal(page);

  return (
    <div
      data-testid="test-selector"
      className="relative min-h-[100dvh] w-full max-w-[520px] mx-auto flex flex-col justify-center px-6 py-10"
    >
      {/* Returning-user LOG IN — restrained account glyph, upper-right, aligned
          to the centered content frame (not the viewport edge). No pill, no
          border, no filled avatar. ~44px tap target; hidden once authenticated. */}
      {onLogIn && (
        <div className="absolute top-3 right-3 z-20">
          <button
            type="button"
            data-testid="nav-login"
            onClick={onLogIn}
            aria-label="Log in"
            title="Log in"
            className="flex h-11 w-11 items-center justify-center rounded-full text-bone/55 hover:text-bone transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
          >
            <User size={22} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>
      )}
      {/* confrontation glows */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-24 top-24 w-72 h-72 rounded-full blur-[90px]"
        style={{ background: "var(--tt-left)", opacity: 0.1 }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 bottom-24 w-72 h-72 rounded-full blur-[90px]"
        style={{ background: "var(--tt-right)", opacity: 0.1 }}
      />

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 flex flex-col items-center"
      >
        <Wordmark variant="stacked" className="h-24 sm:h-28" />

        <h1
          data-testid="selector-heading"
          className="mt-9 font-display font-black uppercase tracking-[-0.02em] text-bone text-2xl sm:text-3xl text-center leading-[1.05]"
        >
          PICK YOUR TERRITORY.
        </h1>
        <p className="mt-3 font-body text-[13px] tracking-wide text-silver text-center">
          One tap begins.
        </p>

        {/* Primary domains */}
        <div className="mt-8 w-full grid grid-cols-3 gap-3">
          {PRIMARY.map((o) => (
            <AccentCard key={o.id} option={o} layout="grid" onSelect={pick} />
          ))}
        </div>

        {/* Combined */}
        <div className="mt-3 w-full grid grid-cols-2 gap-3">
          {COMBINED.map((o) => (
            <AccentCard key={o.id} option={o} layout="row" onSelect={pick} />
          ))}
        </div>

        <p className="mt-8 font-mono text-[10px] tracking-[0.28em] uppercase text-subtle">
          Keep one. Know your taste.
        </p>

        {/* Returning-user personal utility — EXPLORE, visually secondary to PLAY.
            Kept near the footer so the hero hierarchy (logo → PICK YOUR
            TERRITORY → cards) stays uninterrupted. Surfaces only once there is
            meaningful evidence; never a 6th territory card. */}
        {hasEvidence && (onProfile || onKeepers || onEditions) && (
          <div className="mt-5 flex items-center gap-4" data-testid="selector-utility-nav">
            {onProfile && (
              <button
                type="button"
                data-testid="nav-profile"
                onClick={onProfile}
                className="font-mono text-[10px] font-bold tracking-[0.22em] uppercase text-white/55 hover:text-bone transition-colors"
              >
                Profile
              </button>
            )}
            {onProfile && onKeepers && <span className="text-white/15">·</span>}
            {onKeepers && (
              <button
                type="button"
                data-testid="nav-keepers"
                onClick={onKeepers}
                className="font-mono text-[10px] font-bold tracking-[0.22em] uppercase text-white/55 hover:text-bone transition-colors"
              >
                Keepers
              </button>
            )}
            {/* EDITIONS — a post-value personal utility for authenticated
                users only, never shown on the anonymous acquisition path. */}
            {(onProfile || onKeepers) && onEditions && <span className="text-white/15">·</span>}
            {onEditions && (
              <button
                type="button"
                data-testid="nav-editions"
                onClick={onEditions}
                className="font-mono text-[10px] font-bold tracking-[0.22em] uppercase text-white/55 hover:text-bone transition-colors"
              >
                Editions
              </button>
            )}
          </div>
        )}

        <div className="mt-5 flex items-center gap-4 font-body text-[10px] tracking-[0.14em] uppercase text-subtle/80">
          <button type="button" data-testid="footer-privacy" onClick={() => legal("privacy")} className="hover:text-silver transition-colors">Privacy</button>
          <span className="text-white/10">·</span>
          <button type="button" data-testid="footer-terms" onClick={() => legal("terms")} className="hover:text-silver transition-colors">Terms</button>
        </div>
      </motion.div>
    </div>
  );
}
